import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { Loading, Empty, Avatar, StatusPill, useToast, PageHeader, SearchInput } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { formatDate } from '../../lib/format.js';

const ROLES = [
  ['', 'Everyone'],
  ['patient', 'Patients'],
  ['nurse', 'Nurses'],
  ['admin', 'Admins'],
];

export default function Users() {
  const toast = useToast();
  const [role, setRole] = useState('');
  const [q, setQ] = useState('');
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    setRows(null);
    api.get('/api/admin/users', { role, q }).then(setRows).catch(() => setRows([]));
  }, [role, q]);

  useEffect(load, [load]);

  const toggleBlock = async (user) => {
    try {
      const { isBlocked } = await api.post(`/api/admin/users/${user.id}/block`);
      setRows((list) => list.map((u) => (u.id === user.id ? { ...u, isBlocked } : u)));
      toast.success(isBlocked ? `${user.name} suspended.` : `${user.name} reinstated.`);
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <>
      <PageHeader title="Users" subtitle="Manage patient and nurse accounts." />

      <form
        className="result-bar"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(text);
        }}
      >
        <SearchInput value={text} onChange={setText} placeholder="Search name or email…" label="Search users" />
        <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Filter by role" style={{ width: 'auto', flex: '0 1 170px' }}>
          {ROLES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <button className="btn">
          <Icon name="search" /> Search
        </button>
      </form>

      {!rows ? (
        <Loading rows={3} />
      ) : rows.length === 0 ? (
        <Empty art="no-results" title="No users found">
          Try a different search term or role filter.
        </Empty>
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th className="td-right">Bookings</th>
                <th>Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} style={u.isBlocked ? { opacity: 0.6 } : undefined}>
                  <td>
                    <div className="row-tight">
                      <Avatar name={u.name} avatar={u.avatar} size="sm" />
                      <div style={{ minWidth: 0 }}>
                        <div className="bold small truncate">{u.name}</div>
                        <div className="tiny dim truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="pill pill-accent">{u.role}</span>
                  </td>
                  <td>
                    {u.isBlocked ? (
                      <StatusPill status="rejected" label="Suspended" />
                    ) : u.role === 'nurse' ? (
                      <StatusPill status={u.verification_status} label={u.verification_status} />
                    ) : (
                      <StatusPill status="verified" label="Active" />
                    )}
                  </td>
                  <td className="td-right tabular">{u.booking_count}</td>
                  <td className="tiny dim nowrap">{formatDate(u.created_at?.slice(0, 10))}</td>
                  <td>
                    <div className="row-tight" style={{ justifyContent: 'flex-end' }}>
                      {u.role === 'nurse' && (
                        <Link to={`/nurses/${u.id}`} className="btn btn-sm btn-ghost">
                          <Icon name="eye" /> View
                        </Link>
                      )}
                      {u.role !== 'admin' && (
                        <button className={`btn btn-sm ${u.isBlocked ? '' : 'btn-danger'}`} onClick={() => toggleBlock(u)}>
                          {u.isBlocked ? 'Reinstate' : 'Suspend'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
